// Persistence adapter. Phase A uses localStorage; every access is guarded
// because storage can be unavailable (private mode, blocked site data). When it
// is, the app keeps working in memory for the session.
//
// Keys:
//   nell:v1:progress      → progression record (units / domains)
//   nell:v1:lab:<d.u>     → one lab's runtime state (network, consoles, evidence)
//   nell:v1:session       → current sign-in session (see src/auth/session.js)
//   nell:v1:admin:*       → ADMIN / DEVELOPMENT TEST MODE sandbox (never read by learner mode)
//   nell:v1:guest:*       → signed-out visitor's free Support Desk work (temporary, untrusted;
//                           only ever moved into a learner record by src/core/guestClaim.js,
//                           which re-runs the commands and re-validates every ticket)
//
// Keeping each lab in its own key is what makes "reset this lab" surgical: it
// deletes exactly one key and one unit record, nothing else.

const PREFIX = 'nell:v1:';
const mem = new Map();

function ls() {
  try {
    const t = '__nell_probe__';
    window.localStorage.setItem(t, '1');
    window.localStorage.removeItem(t);
    return window.localStorage;
  } catch {
    return null;
  }
}
let backend = typeof window !== 'undefined' ? ls() : null;

/**
 * Create a namespaced store. The learner store and the admin test sandbox use
 * different prefixes, so nothing an admin does can touch learner progress.
 */
export function createStore(prefix = PREFIX) {
  const listeners = new Set();
  const emit = (key) => { for (const f of listeners) { try { f(key); } catch { /* a listener must not break storage */ } } };
  return {
    prefix,
    persistent: () => !!backend,
    get(key, fallback = null) {
      try {
        const raw = backend ? backend.getItem(prefix + key) : mem.get(prefix + key);
        return raw == null ? fallback : JSON.parse(raw);
      } catch {
        return fallback;
      }
    },
    set(key, value) {
      const raw = JSON.stringify(value);
      try {
        if (backend) backend.setItem(prefix + key, raw); else mem.set(prefix + key, raw);
      } catch {
        mem.set(prefix + key, raw); // quota exceeded etc. — degrade to memory
      }
      emit(key);
    },
    remove(key) {
      try { if (backend) backend.removeItem(prefix + key); } catch { /* ignore */ }
      mem.delete(prefix + key);
      emit(key);
    },
    /** Called with the key after every set/remove in this namespace (used by the account sync). */
    onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    /** Keys (without prefix) in this namespace. Excludes nested namespaces such as admin:. */
    keys(sub = '') {
      const out = [];
      const own = (k) => k.startsWith(prefix + sub) && !(prefix === PREFIX && (k.startsWith(ADMIN_PREFIX) || k.startsWith(GUEST_PREFIX)));
      try {
        if (backend) {
          for (let i = 0; i < backend.length; i++) {
            const k = backend.key(i);
            if (own(k)) out.push(k.slice(prefix.length));
          }
        }
      } catch { /* ignore */ }
      for (const k of mem.keys()) if (own(k)) out.push(k.slice(prefix.length));
      return [...new Set(out)];
    },
    /** Test hook: swap the backend (Node tests use a Map-like shim). */
    _use(b) { backend = b; },
  };
}

export const ADMIN_PREFIX = 'nell:v1:admin:';
export const GUEST_PREFIX = 'nell:v1:guest:';
/** Learner progress + lab state (unchanged keys from Domain 01 builds). */
export const store = createStore(PREFIX);
/** Admin / development test sandbox — completely separate keys. */
export const adminStore = createStore(ADMIN_PREFIX);
/** Signed-out visitor (guest) namespace — temporary and never trusted as proof of completion. */
export const guestStore = createStore(GUEST_PREFIX);
